import React, { useEffect, useState, useCallback, useRef } from 'react';
import API from '../services/api'; // 导入统一的API实例
import * as echarts from 'echarts'; // 导入ECharts
import './AdminDashboard.css'; // 导入自定义样式

const AdminDashboard = () => {
  const [userCount, setUserCount] = useState(0);
  const [assignedIpCount, setAssignedIpCount] = useState(0);
  const [trafficData, setTrafficData] = useState([]);
  const [refreshInterval] = useState(600000); // 设置刷新间隔为10分钟
  const [countdown, setCountdown] = useState(600); // 倒计时初始值（秒）
  const lastRefreshTime = useRef(Date.now());
  const trafficChartRef = useRef(null);
  const connectionChartRef = useRef(null);
  const isMobile = window.innerWidth <= 768;
  const lastDataRef = useRef(null);

  const getUnit = (maxValue) => {
    if (maxValue >= 1e9) return { divisor: 1e9, unit: 'Gbps' };
    if (maxValue >= 1e6) return { divisor: 1e6, unit: 'Mbps' };
    if (maxValue >= 1e3) return { divisor: 1e3, unit: 'Kbps' };
    return { divisor: 1, unit: 'bps' };
  };

  const drawTrafficChart = useCallback((chart, dataArray) => {
    if (!chart || !dataArray || dataArray.length === 0) return;

    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? '0' + data.hour : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`;
    });
  
    const inSizeRaw = dataArray.map(data => (data.in_size * 8));
    const indropSizeRaw = dataArray.map(data => (data.in_drop_size * 8));
  
    const maxValue = Math.max(...inSizeRaw, ...indropSizeRaw);
    const { divisor, unit } = getUnit(maxValue);
  
    const inSize = inSizeRaw.map(size => (size / divisor).toFixed(2));
    const indropSize = indropSizeRaw.map(size => (size / divisor).toFixed(2));
  
    const trafficOption = {
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicInOut',
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderColor: '#333',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontSize: 12
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? '0' + hour : hour}:${minute === 0 ? '00' : minute}`;
          let content = `<div style="font-weight: bold; margin-bottom: 5px;">${formattedTime}</div>`;
          params.forEach(item => {
            content += `<div style="display: flex; justify-content: space-between; margin: 3px 0;">
              <span style="margin-right: 15px;">${item.seriesName}:</span>
              <span style="font-weight: bold;">${item.value} ${unit}</span>
            </div>`;
          });
          return content;
        }
      },
      grid: {
        left: isMobile ? '15%' : '10%',
        right: isMobile ? '10%' : '10%',
        bottom: isMobile ? '20%' : '10%',
        top: '15%',
        containLabel: true
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          color: '#666',
          fontSize: isMobile ? 10 : 12,
          margin: 12
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: `{value} ${unit}`,
          color: '#666',
          fontSize: isMobile ? 10 : 12
        },
        splitLine: {
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: `输入流量 (${unit})`,
          type: 'line',
          data: inSize,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          sampling: 'average',
          lineStyle: {
            width: 3,
            color: '#1890ff'
          },
          itemStyle: {
            color: '#1890ff',
            borderWidth: 2,
            borderColor: '#fff'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(24, 144, 255, 0.3)' },
              { offset: 1, color: 'rgba(24, 144, 255, 0.1)' }
            ])
          },
          emphasis: {
            focus: 'series',
            itemStyle: {
              borderWidth: 3
            }
          }
        },
        {
          name: `输入拦截流量 (${unit})`,
          type: 'line',
          data: indropSize,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          sampling: 'average',
          lineStyle: {
            width: 3,
            color: '#52c41a'
          },
          itemStyle: {
            color: '#52c41a',
            borderWidth: 2,
            borderColor: '#fff'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(82, 196, 26, 0.3)' },
              { offset: 1, color: 'rgba(82, 196, 26, 0.1)' }
            ])
          },
          emphasis: {
            focus: 'series',
            itemStyle: {
              borderWidth: 3
            }
          }
        }
      ]
    };
    chart.setOption(trafficOption, true);
  }, [isMobile]);
  
  const drawConnectionChart = useCallback((chart, dataArray) => {
    if (!chart || !dataArray || dataArray.length === 0) return;

    const labels = dataArray.map(data => {
      const formattedHour = data.hour < 10 ? '0' + data.hour : data.hour;
      const formattedMinute = data.minute === 0 ? '00' : data.minute;
      return `${formattedHour}:${formattedMinute}`;
    });
  
    const maxInCount = dataArray.map(data => data.average_connect_in_count);
  
    const connectionOption = {
      animation: true,
      animationDuration: 1000,
      animationEasing: 'cubicInOut',
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        borderColor: '#333',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontSize: 12
        },
        formatter: (params) => {
          const { time, hour, minute } = dataArray[params[0].dataIndex];
          const formattedTime = `${time} ${hour < 10 ? '0' + hour : hour}:${minute === 0 ? '00' : minute}`;
          let content = `<div style="font-weight: bold; margin-bottom: 5px;">${formattedTime}</div>`;
          params.forEach(item => {
            content += `<div style="display: flex; justify-content: space-between; margin: 3px 0;">
              <span style="margin-right: 15px;">${item.seriesName}:</span>
              <span style="font-weight: bold;">${item.value} 个</span>
            </div>`;
          });
          return content;
        }
      },
      grid: {
        left: isMobile ? '15%' : '10%',
        right: isMobile ? '10%' : '10%',
        bottom: isMobile ? '20%' : '10%',
        top: '15%',
        containLabel: true
      },
      legend: {
        show: false
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: labels,
        axisLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        axisLabel: {
          color: '#666',
          fontSize: isMobile ? 10 : 12,
          margin: 12
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#666',
          fontSize: isMobile ? 10 : 12
        },
        splitLine: {
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        }
      },
      series: [
        {
          name: '最大输入新建连接',
          type: 'line',
          data: maxInCount,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          sampling: 'average',
          lineStyle: {
            width: 3,
            color: '#f5222d'
          },
          itemStyle: {
            color: '#f5222d',
            borderWidth: 2,
            borderColor: '#fff'
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(245, 34, 45, 0.3)' },
              { offset: 1, color: 'rgba(245, 34, 45, 0.1)' }
            ])
          },
          emphasis: {
            focus: 'series',
            itemStyle: {
              borderWidth: 3
            }
          }
        }
      ]
    };
    chart.setOption(connectionOption, true);
  }, [isMobile]);
  
  const fetchData = async () => {
    try {
      const userResponse = await API.get('/api/admin/user-count');
      setUserCount(userResponse.data.userCount);

      const ipResponse = await API.get('/api/admin/assigned-ip-count');
      setAssignedIpCount(ipResponse.data.assignedIpCount);

      const dataResponse = await API.get('/api/admin/total-traffic');
      setTrafficData(dataResponse.data.totalTraffic);
      
      lastRefreshTime.current = Date.now();
    } catch (error) {
      console.error('获取数据失败:', error);
    }
  };

  // 初始化图表
  useEffect(() => {
    const initCharts = () => {
      const trafficChartDom = document.getElementById('trafficChart');
      const connectionChartDom = document.getElementById('connectionChart');

      if (trafficChartDom && !trafficChartRef.current) {
        trafficChartRef.current = echarts.init(trafficChartDom);
      }
      if (connectionChartDom && !connectionChartRef.current) {
        connectionChartRef.current = echarts.init(connectionChartDom);
      }
    };

    // 确保DOM已经渲染
    setTimeout(initCharts, 0);

    // 添加resize事件监听
    const handleResize = () => {
      trafficChartRef.current?.resize();
      connectionChartRef.current?.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      trafficChartRef.current?.dispose();
      connectionChartRef.current?.dispose();
    };
  }, []);

  // 监听数据变化并更新图表
  useEffect(() => {
    if (trafficData.length > 0 && trafficChartRef.current && connectionChartRef.current) {
      // 只在数据真正变化时才重绘图表
      const currentData = JSON.stringify(trafficData);
      if (currentData !== lastDataRef.current) {
        lastDataRef.current = currentData;
        
        // 强制清除之前的图表实例
        trafficChartRef.current.clear();
        connectionChartRef.current.clear();

        // 重新绘制图表
        drawTrafficChart(trafficChartRef.current, trafficData);
        drawConnectionChart(connectionChartRef.current, trafficData);
      }
    }
  }, [trafficData, drawTrafficChart, drawConnectionChart]);

  // 数据刷新和倒计时
  useEffect(() => {
    let timer = null;
    let countdownTimer = null;
    const refreshSeconds = refreshInterval / 1000;

    const startRefresh = () => {
      // 立即执行一次数据获取
      fetchData();
      setCountdown(refreshSeconds);

      // 设置定时器定期刷新数据
      timer = setInterval(() => {
        fetchData();
        setCountdown(refreshSeconds);
      }, refreshInterval);

      // 设置倒计时
      countdownTimer = setInterval(() => {
        setCountdown(prev => {
          const newCount = prev - 1;
          if (newCount <= 0) {
            // 当倒计时结束时，清除所有定时器并重新开始
            clearInterval(timer);
            clearInterval(countdownTimer);
            startRefresh();
            return refreshSeconds;
          }
          return newCount;
        });
      }, 1000);
    };

    // 开始刷新循环
    startRefresh();

    // 组件卸载时清理定时器
    return () => {
      if (timer) clearInterval(timer);
      if (countdownTimer) clearInterval(countdownTimer);
    };
  }, [refreshInterval]);

  // 格式化倒计时显示
  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <h1>系统监控面板</h1>
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon users-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-content">
              <h3>用户数量</h3>
              <p className="stat-value">{userCount}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon ip-icon">
              <i className="fas fa-network-wired"></i>
            </div>
            <div className="stat-content">
              <h3>已分配IP数量</h3>
              <p className="stat-value">{assignedIpCount}</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="dashboard-charts">
        <div className="chart-container">
          <div className="chart-header">
            <div className="chart-title">
              <h2>流量监控</h2>
              <div className="refresh-countdown">
                <i className="fas fa-sync-alt"></i>
                <span>{formatCountdown(countdown)}</span>
              </div>
            </div>
            {trafficData.length > 0 && (
              <div className="chart-stats">
                <div className="stats-grid-clean">
                  <div className="stat-card-clean">
                    <div className="stat-header-clean">
                      <i className="fas fa-arrow-down stat-icon-clean input"></i>
                      <span className="stat-title-clean">输入流量</span>
                    </div>
                    <div className="stat-values-clean">
                      <div className="stat-item-clean">
                        <span className="stat-label-clean">峰值</span>
                        <span className="stat-value-clean">
                          {(() => {
                            const inSizeRaw = trafficData.map(data => data.in_size * 8);
                            const maxValue = Math.max(...inSizeRaw);
                            const { divisor, unit } = getUnit(maxValue);
                            return `${(maxValue / divisor).toFixed(2)} ${unit}`;
                          })()}
                        </span>
                      </div>
                      <div className="stat-item-clean">
                        <span className="stat-label-clean">谷值</span>
                        <span className="stat-value-clean">
                          {(() => {
                            const inSizeRaw = trafficData.map(data => data.in_size * 8);
                            const minValue = Math.min(...inSizeRaw);
                            const maxValue = Math.max(...inSizeRaw);
                            const { divisor, unit } = getUnit(maxValue);
                            return `${(minValue / divisor).toFixed(2)} ${unit}`;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="stat-card-clean">
                    <div className="stat-header-clean">
                      <i className="fas fa-shield-alt stat-icon-clean blocked"></i>
                      <span className="stat-title-clean">拦截流量</span>
                    </div>
                    <div className="stat-values-clean">
                      <div className="stat-item-clean">
                        <span className="stat-label-clean">峰值</span>
                        <span className="stat-value-clean">
                          {(() => {
                            const indropSizeRaw = trafficData.map(data => data.in_drop_size * 8);
                            const maxValue = Math.max(...trafficData.map(data => data.in_size * 8), ...indropSizeRaw);
                            const maxDropValue = Math.max(...indropSizeRaw);
                            const { divisor, unit } = getUnit(maxValue);
                            return `${(maxDropValue / divisor).toFixed(2)} ${unit}`;
                          })()}
                        </span>
                      </div>
                      <div className="stat-item-clean">
                        <span className="stat-label-clean">谷值</span>
                        <span className="stat-value-clean">
                          {(() => {
                            const indropSizeRaw = trafficData.map(data => data.in_drop_size * 8);
                            const maxValue = Math.max(...trafficData.map(data => data.in_size * 8), ...indropSizeRaw);
                            const minDropValue = Math.min(...indropSizeRaw);
                            const { divisor, unit } = getUnit(maxValue);
                            return `${(minDropValue / divisor).toFixed(2)} ${unit}`;
                          })()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="chart" id="trafficChart"></div>
          <div className="chart-legend">
            <span className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#1890ff' }}></span>
              输入流量
            </span>
            <span className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#52c41a' }}></span>
              输入拦截流量
            </span>
          </div>
        </div>
        
        <div className="chart-container">
          <div className="chart-header">
            <div className="chart-title">
              <h2>连接监控</h2>
              <div className="refresh-countdown">
                <i className="fas fa-sync-alt"></i>
                <span>{formatCountdown(countdown)}</span>
              </div>
            </div>
          </div>
          <div className="chart" id="connectionChart"></div>
          <div className="chart-legend">
            <span className="legend-item">
              <span className="legend-color" style={{ backgroundColor: '#f5222d' }}></span>
              最大输入新建连接
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
